import { useEffect, useMemo, useState } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Download, FileText } from "lucide-react";
import { CHANNELS, CHANNEL_COLORS } from "@/lib/channels";
import { toast } from "sonner";
import { useCompany } from "@/context/CompanyContext";

const money = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v || 0);

const shortMoney = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);

const GROUP_LABELS = {
  channel: "Channel",
  property: "Property",
  month: "Month",
  payment_status: "Payment Status",
};

export default function Reports() {
  const [properties, setProperties] = useState([]);
  const [groupBy, setGroupBy] = useState("channel");
  const [channel, setChannel] = useState("all");
  const [property, setProperty] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const { company } = useCompany();

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data));
  }, [company]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (channel !== "all") params.channel = channel;
      if (property !== "all") params.property_booked = property;
      if (paymentStatus !== "all") params.payment_status = paymentStatus;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const r = await api.get("/stats/report", { params });
      setReport(r.data);
    } catch (e) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, [groupBy, channel, property, paymentStatus, fromDate, toDate, company]);

  const chartColors = useMemo(() => {
    if (!report) return {};
    const map = {};
    report.rows.forEach((r, i) => {
      map[r.key] = groupBy === "channel"
        ? (CHANNEL_COLORS[r.key] || "#64748B")
        : ["#1E3A8A", "#0369A1", "#0891B2", "#7C3AED", "#B45309", "#334155", "#64748B", "#94A3B8"][i % 8];
    });
    return map;
  }, [report, groupBy]);

  const exportCSV = () => {
    if (!report?.rows?.length) {
      toast.info("Nothing to export");
      return;
    }
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const headers = [GROUP_LABELS[groupBy], "Bookings", "Nights", "Gross", "VAT", "Commission", "Host Payout"];
    const lines = [headers.join(",")];
    for (const r of report.rows) {
      lines.push([r.key, r.count, r.nights, r.gross, r.vat, r.commission, r.payout].map(esc).join(","));
    }
    lines.push(["TOTAL", report.totals.count, report.totals.nights, report.totals.gross, report.totals.vat, report.totals.commission, report.totals.payout].map(esc).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `irentals-report-${groupBy}-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported");
  };

  const clearFilters = () => {
    setChannel("all");
    setProperty("all");
    setPaymentStatus("all");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-6 lg:space-y-8 max-w-[1600px]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Analytics</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500 mt-2">Categorize, filter and export bookings for accounting.</p>
        </div>
        <Button
          onClick={exportCSV}
          className="rounded-sm bg-blue-900 hover:bg-blue-800 text-white active:scale-[0.98]"
          data-testid="report-export-button"
        >
          <Download className="w-4 h-4 mr-2" /> Export Report CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Filters</div>
          <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-blue-900 label-eyebrow" data-testid="clear-filters-btn">Reset</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="space-y-1">
            <Label className="label-eyebrow">Group by</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="group-by-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="channel">Channel</SelectItem>
                <SelectItem value="property">Property</SelectItem>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="payment_status">Payment Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="report-channel-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                {CHANNELS.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">Property</Label>
            <Select value={property} onValueChange={setProperty}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="report-property-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">Payment</Label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger className="rounded-sm border-slate-300 h-10" data-testid="report-payment-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All payments</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="REFUNDED">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm border-slate-300 h-10" data-testid="from-date" />
          </div>
          <div className="space-y-1">
            <Label className="label-eyebrow">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm border-slate-300 h-10" data-testid="to-date" />
          </div>
        </div>
      </div>

      {loading || !report ? (
        <div className="p-8 text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          {/* KPI totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Bookings" value={report.totals.count} testid="rep-count" />
            <Kpi label="Nights" value={report.totals.nights} testid="rep-nights" />
            <Kpi label="Gross Revenue" value={shortMoney(report.totals.gross)} testid="rep-gross" />
            <Kpi label="Host Payout" value={shortMoney(report.totals.payout)} testid="rep-payout" highlight />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="label-eyebrow">Payout by {GROUP_LABELS[groupBy]}</div>
              <h3 className="font-heading text-xl font-medium mt-1 mb-4 text-slate-900">Distribution</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.rows} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${v/1000}k`} />
                    <YAxis dataKey="key" type="category" stroke="#64748B" fontSize={11} width={140} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="payout" radius={[0, 2, 2, 0]}>
                      {report.rows.map((r, i) => (
                        <Cell key={i} fill={chartColors[r.key] || "#1E3A8A"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="label-eyebrow">Booking share by {GROUP_LABELS[groupBy]}</div>
              <h3 className="font-heading text-xl font-medium mt-1 mb-4 text-slate-900">Volume Split</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={report.rows} dataKey="count" nameKey="key" innerRadius={55} outerRadius={100} paddingAngle={2}>
                      {report.rows.map((r, i) => (
                        <Cell key={i} fill={chartColors[r.key] || "#1E3A8A"} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #E2E8F0" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Breakdown table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="p-4 px-6 border-b border-slate-200 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <div className="label-eyebrow">Detailed breakdown by {GROUP_LABELS[groupBy]}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">{GROUP_LABELS[groupBy]}</th>
                    <th className="text-right px-4 py-3 font-semibold">Bookings</th>
                    <th className="text-right px-4 py-3 font-semibold">Nights</th>
                    <th className="text-right px-4 py-3 font-semibold">Gross</th>
                    <th className="text-right px-4 py-3 font-semibold">VAT</th>
                    <th className="text-right px-4 py-3 font-semibold">Commission</th>
                    <th className="text-right px-4 py-3 font-semibold">Host Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-500">No data for these filters.</td></tr>
                  )}
                  {report.rows.map((r) => (
                    <tr key={r.key} className="border-b border-slate-100 hover:bg-blue-50/40 transition-colors" data-testid={`report-row-${r.key}`}>
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: chartColors[r.key] || "#1E3A8A" }} />
                          {r.key}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{r.nights}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(r.gross)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(r.vat)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(r.commission)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-blue-900">{money(r.payout)}</td>
                    </tr>
                  ))}
                  {report.rows.length > 0 && (
                    <tr className="bg-slate-900 text-white font-semibold" data-testid="report-total-row">
                      <td className="px-4 py-3">TOTAL</td>
                      <td className="px-4 py-3 text-right tabular-nums">{report.totals.count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{report.totals.nights}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(report.totals.gross)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(report.totals.vat)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(report.totals.commission)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(report.totals.payout)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-6 ${highlight ? "bg-blue-900 text-white border-blue-900" : "bg-white border-slate-200"}`}
    >
      <div className={`label-eyebrow ${highlight ? "!text-blue-200" : ""}`}>{label}</div>
      <div className="font-heading text-3xl font-semibold tracking-tight mt-3">{value}</div>
    </div>
  );
}
